// ---------------------------------------------------------------------------
// D36: Inbox-specific keyboard shortcuts
// ---------------------------------------------------------------------------
// Active only when in inbox view. Single-key shortcuts:
// R = reply, E = close/resolve, A = assign, S = snooze,
// J = next conversation, K = prev conversation, / = search
// Does NOT conflict with global G+key or N+key system.

import { useEffect, useCallback } from "react";

export interface InboxShortcutActions {
  onReply: () => void;
  onClose: () => void;
  onAssign: () => void;
  onSnooze: () => void;
  onNext: () => void;
  onPrev: () => void;
  onSearch: () => void;
}

/**
 * Register inbox-specific keyboard shortcuts.
 * Only fires when no input/textarea is focused (prevents capture while typing).
 */
export function useInboxShortcuts(actions: InboxShortcutActions) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't capture when typing in input/textarea/contenteditable
    const target = e.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    ) {
      return;
    }

    // Don't capture with modifier keys (Cmd/Ctrl + key = browser shortcut)
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    switch (e.key.toLowerCase()) {
      case "r":
        e.preventDefault();
        actions.onReply();
        break;
      case "e":
        e.preventDefault();
        actions.onClose();
        break;
      case "a":
        e.preventDefault();
        actions.onAssign();
        break;
      case "s":
        e.preventDefault();
        actions.onSnooze();
        break;
      case "j":
        e.preventDefault();
        actions.onNext();
        break;
      case "k":
        e.preventDefault();
        actions.onPrev();
        break;
      case "/":
        e.preventDefault();
        actions.onSearch();
        break;
    }
  }, [actions]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

/** Shortcut definitions for the overlay help panel */
export const INBOX_SHORTCUTS = [
  { key: "R", label: "Reply to conversation" },
  { key: "E", label: "Close / Resolve" },
  { key: "A", label: "Assign to team member" },
  { key: "S", label: "Snooze conversation" },
  { key: "J", label: "Next conversation" },
  { key: "K", label: "Previous conversation" },
  { key: "/", label: "Focus search" },
] as const;
